/**
 * partHighlightLayer – the floating name tags over parts a teacher highlights.
 *
 * A glow says "look here"; the tag says what "here" is. Both have to reach a
 * student on a laptop and one in a headset, so the tag is real geometry in the
 * room rather than a DOM overlay, which has nowhere to live in an immersive
 * session.
 *
 * Choices, and why:
 *
 *   - **Sprites in the room's scene, not children of the model.** A child
 *     would inherit the dock's fit-to-size scale (often a quarter or less) and
 *     come out a different size on every asset. In world space a tag is the
 *     same physical size everywhere: ~4 cm, readable at arm's length in a
 *     headset.
 *   - **Placed from the part's live bounding box every frame.** Explode, a
 *     grab and a student's own scale all move the part; a tag positioned once
 *     would be left floating over empty space. The box is read through the
 *     mesh's current world matrix, so the tag follows whatever moved it.
 *   - **Drawn over everything** (`depthTest: false`, a high `renderOrder`).
 *     A tag sits just above its part, and the part's own neighbours — or the
 *     glowing part itself, seen from above — would otherwise cut it in half.
 *     It stays below the teleport vignette, which must cover everything.
 *   - **Never pickable.** A tag drawn on top of its part would otherwise
 *     intercept the very tap a teacher makes to un-highlight that part, and
 *     three's Sprite raycast needs `raycaster.camera`, which controller rays
 *     do not set.
 *   - **Grows with distance beyond arm's length.** 4 cm is right in a headset
 *     at 0.7 m, but a flat-screen viewer sits two or more metres from the
 *     models, where it shrinks to a smudge. Past `NOMINAL_DISTANCE_M` the tag
 *     keeps a constant apparent size, up to `MAX_GROWTH`.
 *   - **sRGB, not tone-mapped.** The texture is an sRGB image; decoding it as
 *     linear washes the text out, and tone mapping greys the white glyphs.
 *   - **`makeTexture` is injectable,** so node tests can exercise placement
 *     without a canvas.
 */

import * as THREE from 'three';

export interface PartLabel {
  /** The modelTools part id; one tag per part. */
  partId: string;
  text: string;
  /** The part to hang the tag over — `ModelTools.partAnchor(partId).mesh`. */
  mesh: THREE.Mesh;
}

export interface LabelTexture {
  texture: THREE.Texture;
  /** Width over height of the image, so the sprite is not stretched. */
  aspect: number;
}

export interface PartLabelLayerOptions {
  /** The room's scene. Labels are positioned in its (world) space. */
  scene: THREE.Object3D;
  /** Draws one label image. Defaults to a canvas; tests pass a stub. */
  makeTexture?: (text: string) => LabelTexture;
}

export interface PartLabelLayer {
  /** Replace every tag. Cheap enough to call on each highlight change. */
  set(labels: ReadonlyArray<PartLabel>): void;
  /** Follow the parts and face the viewer. Once per frame; `t` is in seconds. */
  update(camera: THREE.Camera, t: number): void;
  /** Remove every tag. The layer stays usable — it belongs to the room. */
  clear(): void;
  /** Remove every tag and the layer itself. Not usable afterwards. */
  dispose(): void;
}

/** Nominal tag height in metres, at or within arm's length. */
export const LABEL_HEIGHT_M = 0.04;
/** Clear air between the top of the part and the bottom of its tag. */
const LABEL_GAP_M = 0.012;
/** Beyond this the tag grows to hold its apparent size. */
const NOMINAL_DISTANCE_M = 0.8;
const MAX_GROWTH = 3;
/** Above the ink (10-12), below the teleport vignette (999). */
export const LABEL_RENDER_ORDER = 900;
/** A tag is a name, not a sentence; a runaway mesh name would span the room. */
const MAX_LABEL_CHARS = 40;

function clampText(text: unknown): string {
  const trimmed = typeof text === 'string' ? text.trim() : '';
  return trimmed.length > MAX_LABEL_CHARS ? `${trimmed.slice(0, MAX_LABEL_CHARS - 1)}…` : trimmed;
}

/**
 * The browser default: white text on a dark rounded pill with an amber edge,
 * matching the default highlight glow. Drawn at twice the pixels the tag
 * covers at arm's length, so it stays crisp as mipmaps take over further out.
 */
function drawLabelTexture(text: string): LabelTexture {
  const height = 96;
  const padding = 28;
  const radius = 22;
  const font = '600 58px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

  const canvas = document.createElement('canvas');
  const measure = canvas.getContext('2d');
  if (measure) measure.font = font;
  const width = Math.ceil((measure?.measureText(text).width ?? text.length * 32) + padding * 2);
  canvas.width = width;
  canvas.height = height;

  // Resizing a canvas resets its context, font included, so style from here on.
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const inset = 3;
    ctx.beginPath();
    ctx.moveTo(radius, inset);
    ctx.arcTo(width - inset, inset, width - inset, height - inset, radius);
    ctx.arcTo(width - inset, height - inset, inset, height - inset, radius);
    ctx.arcTo(inset, height - inset, inset, inset, radius);
    ctx.arcTo(inset, inset, width - inset, inset, radius);
    ctx.closePath();
    ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = 'rgba(255, 200, 61, 0.95)';
    ctx.stroke();

    ctx.font = font;
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, width / 2, height / 2 + 2);
  }

  return { texture: new THREE.CanvasTexture(canvas), aspect: width / height };
}

interface LabelEntry {
  mesh: THREE.Mesh;
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
  texture: THREE.Texture;
  aspect: number;
}

export function createPartLabelLayer(options: PartLabelLayerOptions): PartLabelLayer {
  const { scene } = options;
  const makeTexture = options.makeTexture ?? drawLabelTexture;

  const group = new THREE.Group();
  group.name = 'partLabels';
  scene.add(group);

  let entries: LabelEntry[] = [];
  // Scratch, reused every frame: this runs inside the render loop.
  const box = new THREE.Box3();
  const centre = new THREE.Vector3();
  const eye = new THREE.Vector3();

  const rootOf = (node: THREE.Object3D): THREE.Object3D => {
    let top = node;
    while (top.parent) top = top.parent;
    return top;
  };

  /**
   * Hidden with its model: a tag over a part that is invisible, or no longer
   * in the scene at all, is a name floating over nothing.
   */
  const isShown = (mesh: THREE.Object3D): boolean => {
    for (let node: THREE.Object3D | null = mesh; node; node = node.parent) {
      if (!node.visible) return false;
    }
    return rootOf(mesh) === rootOf(group);
  };

  /** Stand the tag on top of the part's current world-space box. */
  const position = (entry: LabelEntry) => {
    const geometry = entry.mesh.geometry;
    if (!geometry.boundingBox) geometry.computeBoundingBox();
    if (!geometry.boundingBox) return;
    entry.mesh.updateWorldMatrix(true, false);
    box.copy(geometry.boundingBox).applyMatrix4(entry.mesh.matrixWorld);
    box.getCenter(centre);
    entry.sprite.position.set(centre.x, box.max.y + LABEL_GAP_M, centre.z);
  };

  const size = (entry: LabelEntry, growth: number) => {
    const height = LABEL_HEIGHT_M * growth;
    entry.sprite.scale.set(height * entry.aspect, height, 1);
  };

  const clear = () => {
    for (const entry of entries) {
      group.remove(entry.sprite);
      entry.texture.dispose();
      entry.material.dispose();
    }
    entries = [];
  };

  return {
    set(labels) {
      clear();
      const seen = new Set<string>();
      for (const label of labels ?? []) {
        if (!label?.mesh?.geometry || seen.has(label.partId)) continue;
        const text = clampText(label.text);
        if (!text) continue;
        seen.add(label.partId);

        const { texture, aspect } = makeTexture(text);
        texture.colorSpace = THREE.SRGBColorSpace;
        const material = new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
          depthTest: false,
          depthWrite: false,
          toneMapped: false,
        });
        const sprite = new THREE.Sprite(material);
        sprite.name = `partLabel:${label.partId}`;
        // Anchored at the bottom edge, so `position` is where the tag stands.
        sprite.center.set(0.5, 0);
        sprite.renderOrder = LABEL_RENDER_ORDER;
        sprite.raycast = () => {};
        group.add(sprite);

        const entry: LabelEntry = {
          mesh: label.mesh,
          sprite,
          material,
          texture,
          aspect: Number.isFinite(aspect) && aspect > 0 ? aspect : 1,
        };
        entries.push(entry);
        position(entry);
        // Nominal size until the first update knows where the viewer is.
        size(entry, 1);
        sprite.visible = isShown(label.mesh);
      }
    },

    update(camera, t) {
      if (entries.length === 0) return;
      camera.getWorldPosition(eye);
      // A slow breath rather than a blink: enough to catch the eye, never
      // enough to make the text hard to read.
      const opacity = Number.isFinite(t) ? 0.9 + 0.1 * Math.sin(t * Math.PI) : 1;
      for (const entry of entries) {
        const shown = isShown(entry.mesh);
        entry.sprite.visible = shown;
        if (!shown) continue;
        position(entry);
        const distance = entry.sprite.position.distanceTo(eye);
        size(entry, Math.min(MAX_GROWTH, Math.max(1, distance / NOMINAL_DISTANCE_M)));
        entry.material.opacity = opacity;
      }
    },

    clear,

    dispose() {
      clear();
      group.parent?.remove(group);
    },
  };
}
